import React, { useEffect, useRef, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { Box } from '@mui/material';

import { useTranslation } from 'next-i18next';

import type { Ad, GeometryInput } from 'src/types/__generated__/graphql';
import { breakpoints } from 'src/constants/breakpoints';
import { headerHeight } from 'src/constants/misc';
import useModalOpenState from 'src/hooks/useModalOpenState';
import useAlerts from 'src/hooks/useAlerts';
import { AnalyticsEvent } from 'src/utils/analytics';
import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent } from 'src/utils/styled';
import { adsLimitForMap } from 'src/constants/googleMap';
import { defaultListLimit } from 'src/constants/ad';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { useAdScrollRestoration } from 'src/hooks/useAdScrollRestoration';
import { useAds } from '../hooks/useAds';
import { useWindowSize } from '../hooks/useWindowSize';
import { AdList } from './AdList';
import { AdSearchForm } from './AdSearchForm';
import { SearchAdsPageMap } from './SearchAdsPageMap';
import type { ExtendedAdsQueryVariables } from '../types/extendedAdsQueryVariables';
import { AdSearchStateProvider, useAdSearchState } from '../contexts/AdSearchStateContext';
import { useSavedSearchMutations } from '../hooks/useSavedSearchMutations';
import { removeExtraParamsFromAdsQueryVariables } from '../utils/removeExtraParamsFromAdsQueryVariables';
import { adsQueryVariablesToFormValues, formValuesToAdsQueryVariables } from '../utils/adSearchFormUtils';

interface ContainerProps {
    $isHybridView: boolean;
    $mapViewMode: boolean;
}

const Container = styledComponent(Box)<ContainerProps>`
    display: grid;
    height: calc(100vh - ${headerHeight});
    ${({ $isHybridView }) =>
        $isHybridView &&
        `
            grid-template-columns: 35% 65%;
            grid-template-rows: 1fr;
            @media (width >= ${breakpoints.desktopLarge}px) {
                grid-template-columns: 45% 55%;
                grid-template-rows: 1fr;
            }
        `}
    ${({ $isHybridView, $mapViewMode }) =>
        !$isHybridView &&
        $mapViewMode &&
        `
        grid-template-rows: min-content 1fr;
        grid-template-columns: 1fr;
    `}
`;

const FormAndListContainer = styledComponent(Box)`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const scrollRestorationQueryKey = 'restoreAdSearchScroll';

type SearchAdsPageContentInnerProps = {
    isUserAgentBot: boolean;
};
const SearchAdsPageContentInner: React.FC<SearchAdsPageContentInnerProps> = ({ isUserAgentBot }) => {
    const { t } = useTranslation(['ad', 'common']);
    const { addAlert } = useAlerts();
    const scrollerContainerRef = useRef<HTMLDivElement | null>(null);
    const { logEventDynamic } = useDynamicLogEvents();

    const {
        mapBounds,
        selectedSavedSearch,
        adsQueryVariables,
        setHoveredAd,
        viewMode,
        changeAdSearchState,
        loadSavedSearch,
        resetAdSearchState,
    } = useAdSearchState();

    const { props: saveSearchModalProps, open: openSaveSearchModal } = useModalOpenState();

    const { isDesktopSmall, isDesktopLarge } = useWindowSize();

    const { updateSavedSearch } = useSavedSearchMutations();

    const { shouldRestoreScroll, restoreScroll, scrollRestorationRef } = useAdScrollRestoration<HTMLDivElement>({
        delegateExecution: false,
        scrollRestorationQueryKey,
    });
    const fetchPolicy = shouldRestoreScroll ? 'cache-first' : 'cache-and-network';
    const {
        ads,
        totalAds,
        loading: loadingAds,
        fetchMore: fetchMoreAds,
    } = useAds({ queryVariables: adsQueryVariables, fetchPolicy, mapBounds, viewMode });

    const handleMapModeClick = async () => {
        await changeAdSearchState({
            viewMode: 'map',
            fitMapOnAdSearchStateChange: true, // fit the map when switching to map view
        });
        logEventDynamic(AnalyticsEvent.DisplayMap);
    };

    const handleListModeClick = async () => {
        await changeAdSearchState({
            viewMode: 'list',
            mapBounds: null,
        });
        logEventDynamic(AnalyticsEvent.DisplayList);
    };

    type UpdateSearchParams = {
        variables: ExtendedAdsQueryVariables;
        polygonDisplayed?: boolean;
    };
    const updateSearch = async ({ variables, polygonDisplayed }: UpdateSearchParams) => {
        const newValuesAreEqual = isEqual(variables, adsQueryVariables);

        const placeHasChanged =
            !isEqual(variables.places, adsQueryVariables.places) || !isEqual(variables.place, adsQueryVariables.place);

        await changeAdSearchState({
            // if the place has changed, the map must be recentered
            fitMapOnAdSearchStateChange: placeHasChanged,
            ...(polygonDisplayed !== undefined && { isPolygonDisplayed: polygonDisplayed }),
            ...(!newValuesAreEqual && { adsQueryVariables: variables }),
        });
    };

    const clearFilters = async (toDefault?: boolean) => {
        if (selectedSavedSearch && !toDefault) {
            await loadSavedSearch(selectedSavedSearch.id);
        } else {
            await resetAdSearchState();
        }
    };

    const hasResults = Boolean(ads?.length);
    useEffect(() => {
        if (adsQueryVariables.place || adsQueryVariables.places) {
            restoreScroll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- restoreScroll is a function that should be called only once
    }, [hasResults, adsQueryVariables.place, adsQueryVariables.places]);

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement>) => {
        const bottom = isBottomDiv(ev.target as HTMLDivElement);
        const actualAdsLength = ads?.length ?? 0;
        const totalCount = totalAds ?? 0;
        const nextAdsLength = actualAdsLength + defaultListLimit;
        let newLimit = defaultListLimit;
        if (nextAdsLength > adsLimitForMap) {
            newLimit = adsLimitForMap - actualAdsLength;
        }
        const shouldScrollMore =
            actualAdsLength > 0 && actualAdsLength < adsLimitForMap && actualAdsLength < totalCount;
        if (bottom && shouldScrollMore && !loadingAds) {
            await changeAdSearchState({ fitMapOnAdSearchStateChange: true });
            await fetchMoreAds({
                variables: {
                    offset: actualAdsLength,
                    limit: newLimit,
                },
            });
        }
    };

    const handleSaveSearch = async () => {
        if (selectedSavedSearch) {
            const places = adsQueryVariables.places;
            const queryVariablesWithoutExtraParams = removeExtraParamsFromAdsQueryVariables(adsQueryVariables);

            try {
                let place: GeometryInput | undefined;
                if (places && Array.isArray(places) && places[0]) {
                    place = {
                        type: places[0].type,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        coordinates: places[0].coordinates,
                    };
                }
                const parametersUpdated = JSON.stringify({
                    ...queryVariablesWithoutExtraParams,
                    place: {
                        description: t('ad:customBoundary'),
                        cityName: t('ad:customBoundary'),
                    },
                });
                await updateSavedSearch({
                    id: selectedSavedSearch.id,
                    parameters: parametersUpdated,
                    place,
                    placeSource: adsQueryVariables.placesSource,
                });
                addAlert({
                    type: 'success',
                    message: t('ad:searchUpdated'),
                });
                await loadSavedSearch(selectedSavedSearch.id);
                logEventDynamic(AnalyticsEvent.SaveAdSearchConfirmation);
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: String(error),
                });
            }
        } else {
            logEventDynamic(AnalyticsEvent.SaveAdSearch);
            openSaveSearchModal();
        }
    };

    const isHybridView = useMemo(
        () => (isDesktopSmall || isDesktopLarge) && viewMode === 'map',
        [isDesktopLarge, isDesktopSmall, viewMode],
    );

    return (
        <Container $isHybridView={isHybridView} $mapViewMode={viewMode === 'map'}>
            {/* If userAgent is a bot, we don't want to handle scroll behaviour because we display a pagination */}
            <FormAndListContainer ref={scrollerContainerRef} onScroll={!isUserAgentBot ? handleScroll : undefined}>
                <AdSearchForm
                    initialValues={adsQueryVariablesToFormValues(adsQueryVariables)}
                    isHybridView={isHybridView}
                    viewMode={viewMode}
                    totalAds={totalAds ?? undefined}
                    isSaveSearchModalOpen={saveSearchModalProps.open}
                    handleCloseSaveSearchModal={saveSearchModalProps.onClose}
                    handleClearFiltersClick={() => clearFilters()}
                    handleSearchClick={(values) => updateSearch({ variables: formValuesToAdsQueryVariables(values) })}
                    handleToggleViewMode={viewMode === 'list' ? handleMapModeClick : handleListModeClick}
                    handleOrderChange={(newOrderBy) =>
                        updateSearch({
                            variables: {
                                ...adsQueryVariables,
                                orderBy: JSON.parse(newOrderBy) as ExtendedAdsQueryVariables['orderBy'],
                            },
                        })
                    }
                    handleSaveSearch={handleSaveSearch}
                />
                {(isHybridView || viewMode === 'list') && (
                    <AdList
                        ads={ads as Ad[] | undefined}
                        isLoading={loadingAds}
                        handleMouseEnter={(ad) => setHoveredAd(ad)}
                        handleMouseLeave={() => setHoveredAd(null)}
                        scrollRestorationQueryKey={scrollRestorationQueryKey}
                        scrollRestorationRef={scrollRestorationRef}
                        isHybridView={isHybridView}
                        // Only show pagination if the user agent is a bot to allow the bot to navigate through the ads.
                        showPagination={Boolean(totalAds && isUserAgentBot)}
                    />
                )}
            </FormAndListContainer>
            {(isHybridView || viewMode === 'map') && (
                <Box position="relative">
                    <SearchAdsPageMap fetchPolicy={fetchPolicy} />
                </Box>
            )}
        </Container>
    );
};

type SearchAdsPageContentProps = {
    isUserAgentBot: boolean;
};
export const SearchAdsPageContent: React.FC<SearchAdsPageContentProps> = ({ isUserAgentBot }) => (
    <AdSearchStateProvider>
        <SearchAdsPageContentInner isUserAgentBot={isUserAgentBot} />
    </AdSearchStateProvider>
);
